/**
 * ICS-201 Incident Briefing, as an editable card.
 *
 * The form existed only as a generated PDF before this, which meant the initial
 * response record could be printed but not worked on. Rather than build new
 * editors, the card seats the Initial Response sections people already use in
 * the boxes they print into, so Box 5 is the same overview editor the stepper
 * showed and Box 9 is the real roster.
 *
 * Each embedded section keeps its own save path. The card adds no write
 * plumbing of its own; it records a version when one of its own boxes saves,
 * and the sections that own their data record their own.
 */

import React, { useRef } from 'react';

import { useIncident } from 'components/IAP/hooks/incidentHooks';
import { useIncidentInfo } from 'components/IAP/hooks/incidentInfoHooks';
import { useOperationalPeriodWindow } from 'components/IAP/hooks/useOperationalPeriodWindow';
import { useICSContext } from 'components/IAP/planningView/ICSContext';
import { useUscgMapContext } from 'components/MapPage/CorporateViewTabs/LayersTab/UscgMapContext';
import FormVersioningBanner from 'components/IAP/femaCards/FormVersioningBanner';

import Resources from 'components/MapPage/CorporateViewTabs/Resources';

import {
  Card,
  CardAction,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';

import { Ics204CardSection } from '../AssignmentList/ics204BoxLabels';
import { BoxInfo, PrintedValue } from '../icsBoxPrimitives';
import Ics201VersionRecording from '../ics201VersionRecording';
import { IcsPreparedByBox } from '../IcsPreparedByBox';
import {
  ICS201_BOX_LABELS,
  ICS201_DATE_TIME_INITIATED_HINT,
  ICS201_INCIDENT_LOCATION_HINT,
  ICS201_INCIDENT_NAME_HINT,
  ICS201_MAP_SKETCH_HINT,
} from '../ics201BoxLabels';
import Ics201ExportActions from './Ics201ExportActions';
import Ics201MapSketchBox, {
  type Ics201MapSketchHandle,
} from './Ics201MapSketchBox';
import Ics201ObjectivesActionsBox from './Ics201ObjectivesActionsBox';
import Ics201OrgChartBox from './Ics201OrgChartBox';
import Ics201SafetyBox from './Ics201SafetyBox';
import Ics201SituationBox from './Ics201SituationBox';

export const ICS_201_BASE_ID = 'ICS_201';

const Ics201CardBody: React.FC = () => {
  const incident = useIncident();
  const { data: incidentInfo } = useIncidentInfo();
  const { effectivePeriodNumber, readOnlyForms, isFuture } = useICSContext();
  const { isMapVisible } = useUscgMapContext();
  const mapSketchRef = useRef<Ics201MapSketchHandle>(null);

  // Box 3 is the moment the incident began. At period 0 the server's resolver reads `incident_created`, and this
  // mirrors it so the card and the export agree.
  const { opFrom } = useOperationalPeriodWindow();

  const body = (
    <div className="tw:flex tw:min-w-0 tw:flex-col tw:gap-3">
      <FormVersioningBanner baseId={ICS_201_BASE_ID} />

      <div className="tw:grid tw:min-w-0 tw:grid-cols-1 tw:gap-3 tw:md:grid-cols-3">
        <Ics204CardSection
          boxLabel={ICS201_BOX_LABELS.incidentName}
          testId="ics201-section-incident-name"
          className="tw:mt-0 tw:h-full tw:min-w-0"
          headerAction={
            <BoxInfo label="About the incident name">
              {ICS201_INCIDENT_NAME_HINT}
            </BoxInfo>
          }
        >
          <PrintedValue
            value={incident.data?.name ?? ''}
            testId="ics201-field-incident-name"
          />
        </Ics204CardSection>

        <Ics204CardSection
          boxLabel={ICS201_BOX_LABELS.incidentLocation}
          testId="ics201-section-incident-location"
          tour="ics201-location"
          className="tw:mt-0 tw:h-full tw:min-w-0"
          headerAction={
            <BoxInfo label="About the incident location">
              {ICS201_INCIDENT_LOCATION_HINT}
            </BoxInfo>
          }
        >
          <PrintedValue
            value={
              incidentInfo?.incidentLocation || incident.data?.address || ''
            }
            testId="ics201-field-incident-location"
          />
        </Ics204CardSection>

        <Ics204CardSection
          boxLabel={ICS201_BOX_LABELS.dateTimeInitiated}
          testId="ics201-section-date-time-initiated"
          className="tw:mt-0 tw:h-full tw:min-w-0"
          headerAction={
            <BoxInfo label="About the date and time initiated">
              {ICS201_DATE_TIME_INITIATED_HINT}
            </BoxInfo>
          }
        >
          <PrintedValue
            value={opFrom}
            testId="ics201-field-date-time-initiated"
          />
        </Ics204CardSection>
      </div>

      <Ics204CardSection
        boxLabel={ICS201_BOX_LABELS.mapSketch}
        testId="ics201-section-map-sketch"
        tour="ics201-map"
        className="tw:mt-0"
        headerAction={
          <BoxInfo label="About the map and sketch">
            {ICS201_MAP_SKETCH_HINT}
          </BoxInfo>
        }
      >
        <Ics201MapSketchBox ref={mapSketchRef} disabled={readOnlyForms} />
      </Ics204CardSection>

      <Ics204CardSection
        boxLabel={ICS201_BOX_LABELS.currentSituation}
        testId="ics201-section-current-situation"
        tour="ics201-situation"
        className="tw:mt-0"
      >
        <Ics201SituationBox disabled={readOnlyForms} />
      </Ics204CardSection>

      <Ics204CardSection
        boxLabel={ICS201_BOX_LABELS.objectivesAndActions}
        testId="ics201-section-objectives-and-actions"
        tour="ics201-objectives"
        className="tw:mt-0"
      >
        <Ics201ObjectivesActionsBox readOnly={readOnlyForms} />
      </Ics204CardSection>

      <Ics204CardSection
        boxLabel={ICS201_BOX_LABELS.currentOrganization}
        testId="ics201-section-current-organization"
        tour="ics201-organization"
        className="tw:mt-0"
      >
        <Ics201OrgChartBox />
      </Ics204CardSection>

      <Ics204CardSection
        boxLabel={ICS201_BOX_LABELS.resourceSummary}
        testId="ics201-section-resource-summary"
        className="tw:mt-0"
      >
        <Resources assetsOnly />
      </Ics204CardSection>

      <Ics204CardSection
        boxLabel={ICS201_BOX_LABELS.safetyAndHazmat}
        testId="ics201-section-safety-and-hazmat"
        tour="ics201-safety"
        className="tw:mt-0"
      >
        <Ics201SafetyBox disabled={readOnlyForms} />
      </Ics204CardSection>

      <IcsPreparedByBox
        baseId={ICS_201_BASE_ID}
        periodNumber={effectivePeriodNumber}
        boxLabel={ICS201_BOX_LABELS.preparedBy}
        testId="ics201-section-prepared-by"
        tour="ics201-prepared-by"
      />
    </div>
  );

  // Beside the map the planning rail is a narrow column, so cards drop their chrome and render as bare content. Dark
  // mode is class-scoped with no global provider, so it has to be carried here too.
  if (isMapVisible && isFuture) {
    return <div className="dark content tw:text-foreground mt-2">{body}</div>;
  }

  return (
    <Card
      data-testid="uscg-201-card"
      data-tour="ics201-card"
      data-form-family="uscg"
      className="dark tw:bg-background tw:text-foreground"
    >
      <CardHeader>
        <CardTitle>Incident Briefing ICS-201</CardTitle>
        <CardAction>
          <Ics201ExportActions
            captureSketchFile={() =>
              mapSketchRef.current?.captureSketchFile() ??
              Promise.resolve(undefined)
            }
          />
        </CardAction>
      </CardHeader>
      <CardContent>{body}</CardContent>
    </Card>
  );
};

/**
 * The provider wraps the whole card rather than sitting inside it, because the
 * reused sections record through their own mutation hooks and those run
 * wherever the section is mounted, including the drawers the actions table
 * opens.
 */
const Ics201Card: React.FC = () => (
  <Ics201VersionRecording>
    <Ics201CardBody />
  </Ics201VersionRecording>
);

export default Ics201Card;
